import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AstroChart.css';
import { Aspects, Planets, Zodiac } from '../api';
import { degToDMSCompactUnicode, degToDMSString, norm360, signPositions, toRad } from '../helpers/angle-utils';
import { cn } from '../helpers/utils';
import { Checkbox } from './ui/checkbox';

interface Planet {
  name: Planets;
  sign: Zodiac | undefined;
  degree: number;
  house?: number;
}

export interface Aspect {
  from: Planets;
  to: Planets;
  type: Aspects;
  orb: number;
  nature: 'harmonia' | 'desafio';
  strength: number;
}

interface HousePlacement {
  number?: number;
  sign?: Zodiac;
  cuspDegree?: number;
}

interface AstroChartProps {
  planets: Planet[];
  aspects?: Aspect[];
  ascendant?: { sign: Zodiac | undefined; degree: number };
  descendant?: { sign: Zodiac | undefined; degree: number };
  imumCoeli?: { sign: Zodiac | undefined; degree: number };
  mediumCoeli?: { sign: Zodiac | undefined; degree: number };
  houses?: HousePlacement[];
  diameter?: number;

  showPlanetToggles?: boolean; // default true

  /** opcional: afinações do anti-overlap */
  maxRings?: number;            // default 4
  ringStepPlanets?: number;     // default 0.12
  ringStepAngles?: number;      // default 0.10
  jitterSpreadDeg?: number;     // default 1.4 (0 para desligar)
}

function getSignGlyph(sign: Zodiac): string {
  switch (sign) {
    case Zodiac.Aries: return '\u2648';
    case Zodiac.Taurus: return '\u2649';
    case Zodiac.Gemini: return '\u264A';
    case Zodiac.Cancer: return '\u264B';
    case Zodiac.Leo: return '\u264C';
    case Zodiac.Virgo: return '\u264D';
    case Zodiac.Libra: return '\u264E';
    case Zodiac.Scorpio: return '\u264F';
    case Zodiac.Sagittarius: return '\u2650';
    case Zodiac.Capricorn: return '\u2651';
    case Zodiac.Aquarius: return '\u2652';
    case Zodiac.Pisces: return '\u2653';
    default: return '?';
  }
}

function getPlanetGlyph(name: Planets): string {
  switch (name) {
    case Planets.Sun: return '\u2609';
    case Planets.Moon: return '\u263D';
    case Planets.Mercury: return '\u263F';
    case Planets.Venus: return '\u2640';
    case Planets.Mars: return '\u2642';
    case Planets.Jupiter: return '\u2643';
    case Planets.Saturn: return '\u2644';
    case Planets.Uranus: return '\u2645';
    case Planets.Neptune: return '\u2646';
    case Planets.Pluto: return '\u2647';
    case Planets.Earth: return '\u2295';
    case Planets.NorthNode: return '\u260A';
    case Planets.SouthNode: return '\u260B';
    case Planets.Chiron: return '\u26B7';
    case Planets.Ceres: return '\u26B3';
    case Planets.Pallas: return '\u26B4';
    case Planets.Juno: return '\u26B5';
    case Planets.Vesta: return '\u26B6';
    case Planets.Lilith: return '\u26B8';
    case Planets.Pholus: return '\u03C6';
    default: return '?';
  }
}

function polarToCartesian(radius: number, angle: number) {
  const rad = toRad(angle);
  return {
    x: radius * Math.cos(rad),
    y: radius * Math.sin(rad),
  };
}

function describeRingSlice(startAngle: number, endAngle: number, innerRadius: number, outerRadius: number) {
  const startOuter = polarToCartesian(outerRadius, startAngle);
  const endOuter = polarToCartesian(outerRadius, endAngle);
  const startInner = polarToCartesian(innerRadius, startAngle);
  const endInner = polarToCartesian(innerRadius, endAngle);
  const largeArcFlag = norm360(endAngle - startAngle) > 180 ? 1 : 0;

  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endOuter.x} ${endOuter.y}`,
    `L ${endInner.x} ${endInner.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${startInner.x} ${startInner.y}`,
    'Z',
  ].join(' ');
}

function getPlanetTone(name: Planets) {
  switch (name) {
    case Planets.Sun:
      return { fill: '#f6d393', stroke: '#c37a3f', text: '#7d461f' };
    case Planets.Moon:
      return { fill: '#d8e4e4', stroke: '#5b7c7d', text: '#234344' };
    case Planets.Venus:
      return { fill: '#efcfcb', stroke: '#c07061', text: '#74392f' };
    case Planets.Mars:
      return { fill: '#efb7a8', stroke: '#ba6043', text: '#6f2f22' };
    case Planets.Jupiter:
      return { fill: '#cae2df', stroke: '#447f7a', text: '#184642' };
    case Planets.Saturn:
      return { fill: '#e4d6c8', stroke: '#8f6c4e', text: '#5b412d' };
    default:
      return { fill: '#f7f3ed', stroke: '#567275', text: '#17363a' };
  }
}

/** --------- Anti-overlap helpers (aneis + jitter opcional) --------- */

type NamedAngle = { name: Planets; angle: number };

/** Rotaciona a sequência para começar no maior vão angular (estabiliza wrap-around). */
function rotateAtLargestGap(items: NamedAngle[]): NamedAngle[] {
  if (items.length <= 1) return items;
  let pivot = 0;
  let maxGap = -1;
  for (let i = 0; i < items.length; i++) {
    const a = items[i].angle;
    const b = items[(i + 1) % items.length].angle;
    const gap = norm360(b - a); // distância CCW
    if (gap > maxGap) { maxGap = gap; pivot = i + 1; }
  }
  return items.slice(pivot).concat(items.slice(0, pivot));
}

/** Atribuição gulosa de aneis: coloca cada planeta no ring mais baixo sem colidir com o último desse ring. */
function computeRings(
  itemsIn: NamedAngle[],
  deltaDeg: number,
  maxRings: number
): Map<Planets, number> {
  const items = rotateAtLargestGap([...itemsIn].sort((a, b) => a.angle - b.angle));
  const lastAngleByRing: number[] = [];
  const rings = new Map<Planets, number>();

  for (const it of items) {
    let ring = 0;

    // tenta do ring 0 até maxRings-1
    for (; ring < maxRings; ring++) {
      const last = lastAngleByRing[ring];
      if (last == null || norm360(it.angle - last) >= deltaDeg) {
        lastAngleByRing[ring] = it.angle;
        break; // colocado neste ring
      }
    }

    // se não encontrou espaço (ring === maxRings), força para o último ring
    if (ring === maxRings) {
      ring = maxRings - 1;
      lastAngleByRing[ring] = it.angle; // mantém o estado consistente para próximos itens
    }

    rings.set(it.name, ring);
  }

  return rings;
}

/** Opcional: jitter angular dentro de clusters muito densos. Mantém linhas de aspeto intactas. */
function computeAngularJitter(
  itemsIn: NamedAngle[],
  deltaDeg: number,
  spreadDeg: number
): Map<Planets, number> {
  const items = [...itemsIn].sort((a, b) => a.angle - b.angle);
  const jitter = new Map<Planets, number>();
  if (items.length === 0) return jitter;
  // tratar wrap-around com item extra
  const extended = items.concat([{ name: items[0].name, angle: items[0].angle + 360 }]);

  let start = 0;
  for (let i = 1; i < extended.length; i++) {
    const dist = norm360(extended[i].angle - extended[i - 1].angle);
    const end = dist >= deltaDeg || i === extended.length - 1;
    if (end) {
      const len = i - start;
      if (len > 1) {
        const mid = (len - 1) / 2;
        for (let k = 0; k < len; k++) {
          const idx = (start + k) % items.length;
          const off = (k - mid) * spreadDeg;
          jitter.set(items[idx].name, off);
        }
      }
      start = i;
    }
  }
  return jitter;
}

/** ------------------------- Componente ------------------------- */

const AstroChart: React.FC<AstroChartProps> = ({
  planets,
  aspects = [],
  ascendant,
  descendant,
  imumCoeli,
  mediumCoeli,
  houses,
  diameter,

  showPlanetToggles = true,

  // parâmetros do anti-overlap
  maxRings = 4,
  ringStepPlanets = 0.12,
  ringStepAngles = 0.10,
  jitterSpreadDeg = 1.4, // 0 para desligar
}) => {
  const { t } = useTranslation();
  const d = diameter ?? 250;
  const radius = d / 2;

  /** ---- Zoom (scroll no desktop, pinça no mobile) ---- */
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const panState = useRef<{ lastX: number; lastY: number; isPanning: boolean }>({ lastX: 0, lastY: 0, isPanning: false });
  const zoomState = useRef<{ initialDistance: number; startScale: number }>({ initialDistance: 0, startScale: 1 });
  const clampScale = (value: number) => Math.min(3, Math.max(0.6, value));

  const handleWheel = (event: React.WheelEvent) => {
    event.preventDefault();
    const factor = event.deltaY < 0 ? 1.1 : 0.9;
    setScale((prev) => clampScale(prev * factor));
  };

  const distance = (t1: React.Touch, t2: React.Touch) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.hypot(dx, dy);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (event.touches.length === 2) {
      // pinch-zoom
      const dist = distance(event.touches[0], event.touches[1]);
      zoomState.current = { initialDistance: dist, startScale: scale };
      panState.current.isPanning = false; // garantir que não estamos a fazer pan
    } else if (event.touches.length === 1) {
      // pan com um dedo
      const touch = event.touches[0];
      panState.current = {
        lastX: touch.clientX,
        lastY: touch.clientY,
        isPanning: true,
      };
      zoomState.current.initialDistance = 0; // não estamos a fazer pinch
    }
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (event.touches.length === 2 && zoomState.current.initialDistance > 0) {
      // pinch-zoom
      event.preventDefault();
      const dist = distance(event.touches[0], event.touches[1]);
      const next = clampScale(
        (dist / zoomState.current.initialDistance) * zoomState.current.startScale
      );
      setScale(next);
    } else if (event.touches.length === 1 && panState.current.isPanning) {
      // pan
      event.preventDefault();
      const touch = event.touches[0];
      const dx = touch.clientX - panState.current.lastX;
      const dy = touch.clientY - panState.current.lastY;

      panState.current.lastX = touch.clientX;
      panState.current.lastY = touch.clientY;

      setTranslate(prev => ({
        x: prev.x + dx,
        y: prev.y + dy,
      }));
    }
  };

  const handleTouchEnd = () => {
    panState.current.isPanning = false;
    zoomState.current.initialDistance = 0;
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    panState.current = {
      lastX: event.clientX,
      lastY: event.clientY,
      isPanning: true,
    };
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!panState.current.isPanning) return;

    const dx = event.clientX - panState.current.lastX;
    const dy = event.clientY - panState.current.lastY;

    panState.current.lastX = event.clientX;
    panState.current.lastY = event.clientY;

    setTranslate(prev => ({
      x: prev.x + dx,
      y: prev.y + dy,
    }));
  };

  const stopPan = () => {
    panState.current.isPanning = false;
  };

  const handleMouseUp = () => stopPan();
  const handleMouseLeave = () => stopPan();

  /** ---- ROTATION: offset para pôr o ASC à esquerda (9h) ---- */
  const rotationOffset = useMemo(() => {
    const asc = ascendant?.degree;
    return asc == null ? 0 : norm360(180 - norm360(asc));
  }, [ascendant]);
  const rot = (a: number) => norm360(a + rotationOffset);

  const planetPositions = useMemo(
    () =>
      planets.map((p) => ({
        ...p,
        angle: p.degree,
        house: p.house,
      })),
    [planets]
  );

  const [activePlanets, setActivePlanets] = useState<Planets[]>(planets.map((p) => p.name));

  useEffect(() => {
    setActivePlanets(prev => {
      const available = new Set(planets.map(p => p.name));
      const keep = prev.filter(p => available.has(p));
      const newOnes = planets.map(p => p.name).filter(n => !keep.includes(n));
      return [...keep, ...newOnes];
    });
  }, [planets]);

  /** -------- thresholds dinâmicos (escala-invariantes) -------- */
  const baseRPlanets = radius * 0.7;   // onde desenhamos glifos
  const baseRAngles = radius * 0.59;   // onde desenhamos rótulos dos graus
  const approxGlyphSize = 9;           // ajusta ao teu CSS (.glyph/.degree-label)

  const sizeToDeg = (linear: number, r: number) => (linear / (2 * Math.PI * r)) * 360;

  const minDeltaDegPlanets = Math.max(4, sizeToDeg(approxGlyphSize * 1.1, baseRPlanets));

  /** -------- visibilidade e ordenação por ring -------- */
  const visiblePlanets = useMemo(
    () => planetPositions.filter((p) => activePlanets.includes(p.name)),
    [planetPositions, activePlanets]
  );

  const ringsByPlanet = useMemo(() => {
    const items: NamedAngle[] = visiblePlanets.map(p => ({
      name: p.name,
      angle: norm360(p.angle),
    }));
    return computeRings(items, minDeltaDegPlanets, maxRings);
  }, [visiblePlanets, minDeltaDegPlanets, maxRings]);

  const jitterByPlanet = useMemo(() => {
    if (!jitterSpreadDeg) return new Map<Planets, number>();
    const items: NamedAngle[] = visiblePlanets.map(p => ({
      name: p.name,
      angle: norm360(p.angle),
    }));
    return computeAngularJitter(items, minDeltaDegPlanets, jitterSpreadDeg);
  }, [visiblePlanets, minDeltaDegPlanets, jitterSpreadDeg]);

  const visiblePlanetsOrdered = useMemo(() => {
    return [...visiblePlanets].sort((a, b) => {
      const ra = ringsByPlanet.get(a.name) ?? 0;
      const rb = ringsByPlanet.get(b.name) ?? 0;
      if (ra !== rb) return ra - rb; // desenha internos primeiro; externos por cima
      return a.angle - b.angle;
    });
  }, [visiblePlanets, ringsByPlanet]);

  /** -------- coordenadas (com rotação aplicada) -------- */
  const planetCoords = useMemo(() => {
    const baseR = baseRPlanets;
    const coords: Record<Planets, { x: number; y: number }> = {
      Ceres: { x: 0, y: 0 },
      Chiron: { x: 0, y: 0 },
      Earth: { x: 0, y: 0 },
      Juno: { x: 0, y: 0 },
      Jupiter: { x: 0, y: 0 },
      Lilith: { x: 0, y: 0 },
      Mars: { x: 0, y: 0 },
      Mercury: { x: 0, y: 0 },
      Moon: { x: 0, y: 0 },
      Neptune: { x: 0, y: 0 },
      NorthNode: { x: 0, y: 0 },
      Pallas: { x: 0, y: 0 },
      Pholus: { x: 0, y: 0 },
      Pluto: { x: 0, y: 0 },
      Saturn: { x: 0, y: 0 },
      SouthNode: { x: 0, y: 0 },
      Sun: { x: 0, y: 0 },
      Uranus: { x: 0, y: 0 },
      Venus: { x: 0, y: 0 },
      Vesta: { x: 0, y: 0 },
    };

    planetPositions.forEach((planet) => {
      const ring = ringsByPlanet.get(planet.name) ?? 0;
      const jitter = jitterByPlanet.get(planet.name) ?? 0;
      const effectiveAngle = rot(planet.angle + jitter); // rotação aplicada
      const r = baseR * (1 - ringStepPlanets * ring);
      const rad = toRad(effectiveAngle);
      coords[planet.name] = { x: r * Math.cos(rad), y: r * Math.sin(rad) };
    });
    return coords;
  }, [planetPositions, ringsByPlanet, jitterByPlanet, baseRPlanets, ringStepPlanets, rotationOffset]);

  const planetAngleCoords = useMemo(() => {
    const baseR = baseRAngles;
    const coords: Record<Planets, { x: number; y: number }> = {
      Ceres: { x: 0, y: 0 },
      Chiron: { x: 0, y: 0 },
      Earth: { x: 0, y: 0 },
      Juno: { x: 0, y: 0 },
      Jupiter: { x: 0, y: 0 },
      Lilith: { x: 0, y: 0 },
      Mars: { x: 0, y: 0 },
      Mercury: { x: 0, y: 0 },
      Moon: { x: 0, y: 0 },
      Neptune: { x: 0, y: 0 },
      NorthNode: { x: 0, y: 0 },
      Pallas: { x: 0, y: 0 },
      Pholus: { x: 0, y: 0 },
      Pluto: { x: 0, y: 0 },
      Saturn: { x: 0, y: 0 },
      SouthNode: { x: 0, y: 0 },
      Sun: { x: 0, y: 0 },
      Uranus: { x: 0, y: 0 },
      Venus: { x: 0, y: 0 },
      Vesta: { x: 0, y: 0 },
    };

    planetPositions.forEach((planet) => {
      const ring = ringsByPlanet.get(planet.name) ?? 0;
      const jitter = jitterByPlanet.get(planet.name) ?? 0;
      const effectiveAngle = rot(planet.angle + jitter);
      const r = baseR * (1 - ringStepAngles * ring);
      const rad = toRad(effectiveAngle);
      coords[planet.name] = { x: r * Math.cos(rad), y: r * Math.sin(rad) };
    });
    return coords;
  }, [planetPositions, ringsByPlanet, jitterByPlanet, baseRAngles, ringStepAngles, rotationOffset]);

  /** As linhas de aspetos mantêm o raio fixo, mas também devem acompanhar a rotação. */
  const aspectCoords = useMemo(() => {
    const r = radius * 0.5;
    const coords: Record<Planets, { x: number; y: number }> = {
      Ceres: { x: 0, y: 0 },
      Chiron: { x: 0, y: 0 },
      Earth: { x: 0, y: 0 },
      Juno: { x: 0, y: 0 },
      Jupiter: { x: 0, y: 0 },
      Lilith: { x: 0, y: 0 },
      Mars: { x: 0, y: 0 },
      Mercury: { x: 0, y: 0 },
      Moon: { x: 0, y: 0 },
      Neptune: { x: 0, y: 0 },
      NorthNode: { x: 0, y: 0 },
      Pallas: { x: 0, y: 0 },
      Pholus: { x: 0, y: 0 },
      Pluto: { x: 0, y: 0 },
      Saturn: { x: 0, y: 0 },
      SouthNode: { x: 0, y: 0 },
      Sun: { x: 0, y: 0 },
      Uranus: { x: 0, y: 0 },
      Venus: { x: 0, y: 0 },
      Vesta: { x: 0, y: 0 },
    };

    planetPositions.forEach((planet) => {
      const rad = toRad(rot(planet.angle));
      coords[planet.name] = { x: r * Math.cos(rad), y: r * Math.sin(rad) };
    });
    return coords;
  }, [planetPositions, radius, rotationOffset]);

  /** -------- UI helpers -------- */
  const palette = {
    shell: '#fbf6ee',
    shellInner: '#fffdfa',
    line: '#887761',
    lineSoft: 'rgba(136, 119, 97, 0.42)',
    axis: '#425f63',
    axisSoft: 'rgba(66, 95, 99, 0.2)',
    harmony: '#2f8b7a',
    challenge: '#d06f4e',
    core: '#f1ddc4',
  };

  const natureColors: Record<'harmonia' | 'desafio', string> = {
    harmonia: palette.harmony,
    desafio: palette.challenge,
  };

  const togglePlanet = (name: Planets) => {
    setActivePlanets((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  const signOffsets = [0, 5, 10, 15, 20, 25] as const;
  const zodiacBandColors = ['rgba(194, 107, 60, 0.07)', 'rgba(79, 133, 137, 0.07)'] as const;
  const tickCfg: Record<number, { inner: number; outer: number }> = {
    0: { inner: 0.8, outer: 1.0 },
    5: { inner: 0.8, outer: 0.82 },
    10: { inner: 0.8, outer: 0.85 },
    15: { inner: 0.8, outer: 0.82 },
    20: { inner: 0.8, outer: 0.85 },
    25: { inner: 0.8, outer: 0.82 },
  };

  const backgroundStars = useMemo(
    () => [
      { x: -radius * 0.62, y: -radius * 0.72, r: 1.5 },
      { x: radius * 0.68, y: -radius * 0.54, r: 1.25 },
      { x: -radius * 0.78, y: 0, r: 1.15 },
      { x: radius * 0.73, y: radius * 0.12, r: 1.2 },
      { x: -radius * 0.38, y: radius * 0.74, r: 1.3 },
      { x: radius * 0.24, y: radius * 0.82, r: 1.55 },
    ],
    [radius]
  );

  function AxisLine({ angle, inner, outer, k, stroke = palette.axis, width = 1 }: {
    angle?: number; inner: number; outer: number; k: string; stroke?: string; width?: number;
  }) {
    if (angle == null) return null;
    const r = toRad(rot(angle));
    return (
      <line
        key={`${k}-${angle}`}
        x1={inner * Math.cos(r)} y1={inner * Math.sin(r)}
        x2={outer * Math.cos(r)} y2={outer * Math.sin(r)}
        stroke={stroke} strokeWidth={width} vectorEffect="non-scaling-stroke"
      />
    );
  }

  function AxisLabel({ angle, r, text, k }: {
    angle?: number; r: number; text: string; k: string;
  }) {
    if (angle == null) return null;
    const ang = toRad(rot(angle));
    return <text key={`${k}-${text}`} x={r * Math.cos(ang)} y={r * Math.sin(ang)} className="cardinal-label">{text}</text>;
  }

  function AxisDegLabel({ angle, sign, r, k }: {
    angle?: number; sign?: Zodiac; r: number; k: string;
  }) {
    if (angle == null || !sign) return null;
    const ang = toRad(rot(angle));
    const txt = degToDMSCompactUnicode(angle - (signPositions[sign] ?? 0));
    return <text key={`${k}-deg`} x={r * Math.cos(ang)} y={r * Math.sin(ang)} className="cardinal-label">{txt}</text>;
  }

  return (
    <div className="astro-chart-wrapper">
      <div
        className="astro-chart-container mb-6 md:mb-4"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <svg
          aria-label={t('chart_natal_description')}
          viewBox={`${-radius - 25} ${-radius - 25} ${2 * radius + 50} ${2 * radius + 50}`}
          className="astro-chart"
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          height="100%"
        >
          <defs>
            <radialGradient id="astro-shell-glow" cx="50%" cy="48%" r="65%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="62%" stopColor="#fbf4e8" />
              <stop offset="100%" stopColor="#ead9c0" />
            </radialGradient>
            <radialGradient id="astro-core-glow" cx="40%" cy="35%" r="75%">
              <stop offset="0%" stopColor="#fffdf7" />
              <stop offset="58%" stopColor={palette.core} />
              <stop offset="100%" stopColor="#e2c9a5" />
            </radialGradient>
            <filter id="astro-planet-shadow" x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="2" stdDeviation="2.6" floodColor="rgba(23, 54, 58, 0.16)" />
            </filter>
          </defs>
          <g transform={`translate(${translate.x}, ${translate.y}) scale(${scale})`}>
            <circle cx="0" cy="0" r={radius * 1.02} fill="url(#astro-shell-glow)" />
            {backgroundStars.map((star, index) => (
              <circle
                key={`star-${index}`}
                cx={star.x}
                cy={star.y}
                r={star.r}
                fill="rgba(255, 255, 255, 0.92)"
                stroke="rgba(66, 95, 99, 0.22)"
                strokeWidth="0.25"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {Object.entries(signPositions).map(([sign, position], index) => (
              <path
                key={`sign-band-${sign}`}
                d={describeRingSlice(rot(position), rot(position + 30), radius * 0.82, radius * 0.985)}
                fill={zodiacBandColors[index % zodiacBandColors.length]}
                stroke="rgba(136, 119, 97, 0.08)"
                strokeWidth="0.4"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <circle cx="0" cy="0" r={radius} fill="none" stroke={palette.line} strokeWidth="1.15" vectorEffect="non-scaling-stroke" />
            <circle
              cx="0"
              cy="0"
              r={radius * 0.88}
              fill="none"
              stroke={palette.axisSoft}
              strokeWidth="0.7"
              strokeDasharray="1.5 3.4"
              vectorEffect="non-scaling-stroke"
            />
            <circle cx="0" cy="0" r={radius * 0.8} fill={palette.shellInner} stroke={palette.lineSoft} strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
            <circle cx="0" cy="0" r={radius * 0.56} fill="none" stroke={palette.axisSoft} strokeWidth="0.5" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
            <circle cx="0" cy="0" r={radius * 0.15} fill="url(#astro-core-glow)" stroke={palette.lineSoft} strokeWidth="0.8" vectorEffect="non-scaling-stroke" />

            {/* House lines (from calculated cusps) */}
            {Array.isArray(houses) && houses.length > 0 && houses.map(({ number, cuspDegree }) => {
              if (cuspDegree == null) return null;
              const rad = toRad(rot(cuspDegree));
              return (
                <line
                  key={`house-line-${number ?? 'na'}`}
                  x1={radius * 0.15 * Math.cos(rad)} y1={radius * 0.15 * Math.sin(rad)}
                  x2={radius * 0.8 * Math.cos(rad)} y2={radius * 0.8 * Math.sin(rad)}
                  stroke={palette.lineSoft} strokeWidth="0.55" vectorEffect="non-scaling-stroke"
                />
              );
            })}

            {/* House numbers centered using adjacent cusps */}
            {Array.isArray(houses) && houses.length > 1 && houses.map((cusp, idx) => {
              const a = houses[idx]?.cuspDegree;
              const b = houses[(idx + 1) % houses.length]?.cuspDegree;
              if (a == null || b == null) return null;
              const aN = norm360(a); const bN = norm360(b);
              const delta = norm360(bN - aN);
              const mid = rot(norm360(aN + delta / 2));
              const rad = toRad(mid);
              return (
                <text
                  key={`house-label-${cusp.number ?? idx + 1}`}
                  x={radius * 0.20 * Math.cos(rad)} y={radius * 0.20 * Math.sin(rad)}
                  className="house-label" textAnchor="middle" dominantBaseline="middle">
                  {cusp.number ?? idx + 1}
                </text>
              );
            })}

            {/* AC */}
            <AxisLine k="AC-in" angle={ascendant?.degree} inner={radius * 0.15} outer={radius * 0.8} width={0.8} />
            <AxisLine k="AC-out" angle={ascendant?.degree} inner={radius} outer={radius * 1.05} width={1.1} />
            <AxisLabel k="AC" angle={ascendant?.degree} r={radius + 11} text="AC" />
            <AxisDegLabel k="ACdeg" angle={ascendant?.degree} sign={ascendant?.sign} r={radius + 21} />

            {/* DC */}
            <AxisLine k="DC-in" angle={descendant?.degree} inner={radius * 0.15} outer={radius * 0.8} width={0.8} />
            <AxisLine k="DC-out" angle={descendant?.degree} inner={radius} outer={radius * 1.05} width={1.1} />
            <AxisLabel k="DC" angle={descendant?.degree} r={radius + 11} text="DC" />
            <AxisDegLabel k="DCdeg" angle={descendant?.degree} sign={descendant?.sign} r={radius + 21} />

            {/* IC */}
            <AxisLine k="IC-in" angle={imumCoeli?.degree} inner={radius * 0.15} outer={radius * 0.8} width={0.8} />
            <AxisLine k="IC-out" angle={imumCoeli?.degree} inner={radius} outer={radius * 1.05} width={1.1} />
            <AxisLabel k="IC" angle={imumCoeli?.degree} r={radius + 11} text="IC" />
            <AxisDegLabel k="ICdeg" angle={imumCoeli?.degree} sign={imumCoeli?.sign} r={radius + 21} />

            {/* MC */}
            <AxisLine k="MC-in" angle={mediumCoeli?.degree} inner={radius * 0.15} outer={radius * 0.8} width={0.8} />
            <AxisLine k="MC-out" angle={mediumCoeli?.degree} inner={radius} outer={radius * 1.05} width={1.1} />
            <AxisLabel k="MC" angle={mediumCoeli?.degree} r={radius + 11} text="MC" />
            <AxisDegLabel k="MCdeg" angle={mediumCoeli?.degree} sign={mediumCoeli?.sign} r={radius + 21} />

            {/* Aspect lines */}
            {aspects
              .filter((a) => activePlanets.includes(a.from) && activePlanets.includes(a.to))
              .map((aspect, index) => {
                const from = aspectCoords[aspect.from];
                const to = aspectCoords[aspect.to];
                if (!from || !to) return null;
                return (
                  <line
                    key={`aspect-${index}`}
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke={natureColors[aspect.nature]}
                    strokeWidth={0.5 + aspect.strength}
                    strokeDasharray={aspect.nature === 'desafio' ? '4 3' : undefined}
                    strokeLinecap="round"
                    opacity={aspect.nature === 'desafio' ? 0.82 : 0.74}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}

            {/* Zodiac glyphs around the outer rim (mid-sign positions) */}
            {Object.entries(signPositions).map(([sign, position]) => {
              const mid = rot(position + 15);
              const rad = toRad(mid);
              const rSign = radius * 0.92;
              const x = rSign * Math.cos(rad);
              const y = rSign * Math.sin(rad);
              return (
                <g key={`sign-${sign}`} aria-label={t('sign_' + sign)}>
                  <circle
                    cx={x}
                    cy={y}
                    r={8}
                    fill="rgba(255, 255, 255, 0.78)"
                    stroke="rgba(66, 95, 99, 0.18)"
                    strokeWidth="0.45"
                    vectorEffect="non-scaling-stroke"
                  />
                  <text
                    x={x}
                    y={y}
                    className="sign-glyph"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {getSignGlyph(sign as Zodiac)}
                    <title>{t('sign_' + sign)}</title>
                  </text>
                </g>
              );
            })}

            {/* Sign boundary ticks */}
            {signOffsets.flatMap(offset =>
              Object.values(signPositions).map((position) => {
                const rad = toRad(rot(position + offset));
                const inner = radius * tickCfg[offset].inner;
                const outer = radius * tickCfg[offset].outer;
                return (
                    <line
                      key={`tick-${position}-${offset}`}
                      x1={inner * Math.cos(rad)} y1={inner * Math.sin(rad)}
                      x2={outer * Math.cos(rad)} y2={outer * Math.sin(rad)}
                      stroke={offset === 0 ? palette.line : palette.lineSoft}
                      strokeWidth={offset === 0 ? 0.6 : 0.42}
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })
            )}

            {/* Planets (filtered by checkbox) */}
            {visiblePlanetsOrdered.map((planet) => {
              const { x, y } = planetCoords[planet.name];
              const signAngle = planet.sign ? signPositions[planet.sign] : 0;
              const tone = getPlanetTone(planet.name);
              const label = `${t('planet_' + planet.name)} - ${t('sign_' + planet.sign)} ${degToDMSString(planet.degree - signAngle)}${planet.house ? ' (' + t('house_' + planet.house) + ')' : ''}`;
              return (
                <g key={planet.name} transform={`translate(${x},${y})`} aria-label={label}>
                  <circle
                    r={6.8}
                    fill={tone.fill}
                    stroke={tone.stroke}
                    strokeWidth="0.8"
                    vectorEffect="non-scaling-stroke"
                    filter="url(#astro-planet-shadow)"
                  />
                  <text className="glyph" fill={tone.text}>{getPlanetGlyph(planet.name)}</text>
                  <title>{label}</title>
                </g>
              );
            })}

            {visiblePlanetsOrdered.map((planet) => {
              const { x, y } = planetAngleCoords[planet.name];
              const signAngle = planet.sign ? signPositions[planet.sign] : 0;
              return (
                <text
                  key={`angle-${planet.name}`}
                  className="degree-label"
                  x={x}
                  y={y}
                >
                  {degToDMSCompactUnicode(planet.degree - signAngle)}
                </text>
              );
            })}

            {/* Pequeno tick radial do planeta na borda, rotacionado (usa jitter para alinhar visualmente) */}
            {visiblePlanetsOrdered.map((planet) => {
              const jitter = jitterByPlanet.get(planet.name) ?? 0;
              const ang = rot(planet.degree + jitter);
              const rad = toRad(ang);
              return (
                <line
                  key={`planet-line-${planet.name ?? 'na'}`}
                  x1={radius * 0.78 * Math.cos(rad)} y1={radius * 0.78 * Math.sin(rad)}
                  x2={radius * 0.8 * Math.cos(rad)} y2={radius * 0.8 * Math.sin(rad)}
                  stroke={palette.axis} strokeWidth="0.55" vectorEffect="non-scaling-stroke"
                />
              );
            })}
          </g>
        </svg>
      </div>

      <div className="mt-2 flex items-center justify-center gap-4 pb-1 text-sm md:mt-0 md:pb-0">
        <div className="flex items-center gap-2">
          <div className="aspect-key aspect-key--harmony"></div>
          <span className="text-slate-600">{t('aspect_harmony')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="aspect-key aspect-key--challenge"></div>
          <span className="text-slate-600">{t('aspect_challenge')}</span>
        </div>
      </div>

      {showPlanetToggles && (
        <div className="flex flex-wrap items-center justify-center gap-2.5 border-t border-stone-200 pt-4">
          {planets.map((p) => {
            const isActive = activePlanets.includes(p.name);
            return (
            <label
              key={p.name}
              htmlFor={p.name}
              className={cn(
                'flex cursor-pointer select-none items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-amber-200 bg-amber-50 text-amber-800 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              )}
            >
              <Checkbox
                id={p.name}
                checked={activePlanets.includes(p.name)}
                onCheckedChange={() => togglePlanet(p.name)}
                className={cn(
                  'h-5 w-5 rounded-md border text-white shadow-none ring-offset-0',
                  'focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:ring-offset-0',
                  isActive
                    ? 'border-amber-500 bg-amber-500 data-[state=checked]:border-amber-500 data-[state=checked]:bg-amber-500'
                    : 'border-slate-300 bg-white text-transparent data-[state=unchecked]:bg-white'
                )}
              />
              <span className={cn(
                'leading-none',
                isActive ? 'text-amber-800' : 'text-slate-700'
              )}>
                {t("planet_" + p.name)}
              </span>
            </label>
          )})}
        </div>
      )}

    </div>
  );
};

export default React.memo(AstroChart);
